import {
  action$,
  collectRoutines,
  dispatchAction,
  routine,
  ofType,
  extractPayload,
} from '@ardoq/rxbeach';
import {
  createFilterRequested,
  includeComponentTypeRequested,
  includeReferenceTypeRequested,
  notifyFiltersChanged,
  triggerFiltersChangedEvent,
  updateFiltered,
} from './FilterActions';
import { filter as filterStream, merge, tap, withLatestFrom } from 'rxjs';
import { globalFilters as Filters } from 'collections/filters';
import { perspectives as Perspectives } from 'collections/perspectives';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { groupingRuleOperations } from '@ardoq/core';
import { filterInterface } from '@ardoq/filter-interface';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { context$ } from 'streams/context/context$';
import { activeFilter$ } from './activeFilter$';
import { sort$ } from 'streams/context/sort$';
import { tagEntityChanges$ } from 'streams/tags/tags$';
import { references$ } from 'streams/references/references$';
import { websocket$ } from 'sync/websocket$';
import { isWebSocketGlobalFieldRenamed } from 'sync/types';
import { fieldInterface } from '@ardoq/field-interface';
import { ensureComponentTypesIncluded } from 'quickPerspectives/legends/componentFilterRuleManager';
import { ensureReferenceTypeIncluded } from 'quickPerspectives/legends/referenceFilterRuleManager';
import {
  ArdoqId,
  FilterAttributes,
  filterAttributesIsSubquery,
  isWorkspaceFilter,
  WorkspaceFilterAttributes,
} from '@ardoq/api-types';

const notifyFiltersChangeOnTrigger = merge(
  action$.pipe(ofType(triggerFiltersChangedEvent)),
  references$
).pipe(
  withLatestFrom(context$, activeFilter$, groupingRules$),
  tap(([_, context, activeFilterState, { groupingRules }]) =>
    dispatchAction(
      notifyFiltersChanged({
        filters: Filters.getFiltersAsQueryBuilderQueries(),
        queryString: Perspectives.encodeURI(
          activeFilterState,
          context.workspaceId,
          groupingRuleOperations.getValidRules(groupingRules)
        ),
      })
    )
  )
);

const notifyFiltersChangeOnSortChange = sort$.pipe(
  withLatestFrom(context$, activeFilter$, groupingRules$),
  tap(([_, context, activeFilterState, { groupingRules }]) =>
    dispatchAction(
      notifyFiltersChanged({
        filters: Filters.getFiltersAsQueryBuilderQueries(),
        queryString: Perspectives.encodeURI(
          activeFilterState,
          context.workspaceId,
          groupingRuleOperations.getValidRules(groupingRules)
        ),
      })
    )
  )
);

const isFieldReferencingFilter = (
  filter: FilterAttributes,
  fieldName: string
): boolean => {
  if (filterAttributesIsSubquery(filter)) {
    return filter.rules.some(rule => isFieldReferencingFilter(rule, fieldName));
  }
  return filter.name === fieldName;
};

const renameFieldInFilter = (
  filter: FilterAttributes,
  oldName: string,
  newName: string
): FilterAttributes => {
  if (filterAttributesIsSubquery(filter)) {
    return {
      ...filter,
      rules: filter.rules.map(rule =>
        renameFieldInFilter(rule, oldName, newName)
      ),
    };
  }
  if (filter.name !== oldName) {
    return filter;
  }
  return { ...filter, name: newName };
};

const asWorkspaceFilterOrUndefined = (
  workspaceFilter: FilterAttributes | null
): WorkspaceFilterAttributes | undefined =>
  workspaceFilter && isWorkspaceFilter(workspaceFilter)
    ? workspaceFilter
    : undefined;

const migrateActiveFilters = (oldName: string, newName: string): void => {
  const { advancedFilters, workspaceFilter } = Filters.getPersistedFilters();

  const renamedFieldIsFiltered = advancedFilters.some(filter =>
    isFieldReferencingFilter(filter, oldName)
  );
  if (!renamedFieldIsFiltered) {
    return;
  }

  Filters.loadFilters({
    advancedFilters: advancedFilters.map(filter =>
      renameFieldInFilter(filter, oldName, newName)
    ),
    workspaceFilter: asWorkspaceFilterOrUndefined(workspaceFilter),
    shouldTriggerChangeEvent: true,
  });
};

const workspaceHasFieldNamed = (
  workspaceId: ArdoqId,
  fieldName: string
): boolean =>
  fieldInterface
    .getAllFieldsOfWorkspace(workspaceId)
    .some(field => field.name === fieldName);

// A filter migration is only harmful when it rewrites a filter that still
// works: if a context workspace has a live field named oldName, that field was
// not the one renamed (rename events are broadcast org-wide), and rewriting its
// filter to newName would break it. Migrating is safe precisely when no context
// workspace still has a field named oldName.
const oldNameAbsentFromContext = (
  oldName: string,
  contextWorkspaceIds: ArdoqId[]
): boolean =>
  !contextWorkspaceIds.some(workspaceId =>
    workspaceHasFieldNamed(workspaceId, oldName)
  );

const handleFieldRenamed = websocket$.pipe(
  filterStream(isWebSocketGlobalFieldRenamed),
  withLatestFrom(context$),
  tap(
    ([
      {
        data: { oldName, newName },
      },
      context,
    ]) => {
      Perspectives.fetch();
      if (oldNameAbsentFromContext(oldName, context.workspaceIds)) {
        migrateActiveFilters(oldName, newName);
      }
    }
  )
);

const handleUpdateFiltered = routine(
  ofType(updateFiltered),
  extractPayload(),
  withLatestFrom(loadedGraph$),
  tap(([{ componentId }, loadedGraph]) => {
    filterInterface.updateFiltered(componentId, loadedGraph);
  })
);

const handleTagEntityChanges = tagEntityChanges$.pipe(
  withLatestFrom(loadedGraph$),
  tap(([entityIds, loadedGraph]) => {
    entityIds.forEach(id => filterInterface.updateFiltered(id, loadedGraph));
  })
);

const handleCreateFilterRequested = routine(
  ofType(createFilterRequested),
  extractPayload(),
  tap(attributes =>
    filterInterface.createFilter(attributes, { shouldTriggerChangeEvent: true })
  )
);

const handleIncludeComponentTypeRequested = routine(
  ofType(includeComponentTypeRequested),
  extractPayload(),
  tap(componentTypeName =>
    ensureComponentTypesIncluded(
      [componentTypeName],
      filterInterface.getFiltersAsQueryBuilderQueries().componentRules
    )
  )
);

const handleIncludeReferenceTypeRequested = routine(
  ofType(includeReferenceTypeRequested),
  extractPayload(),
  tap(referenceTypeName =>
    ensureReferenceTypeIncluded(
      referenceTypeName,
      filterInterface.getFiltersAsQueryBuilderQueries().referenceRules
    )
  )
);

export const filterRoutines = collectRoutines(
  notifyFiltersChangeOnTrigger,
  notifyFiltersChangeOnSortChange,
  handleFieldRenamed,
  handleUpdateFiltered,
  handleTagEntityChanges,
  handleCreateFilterRequested,
  handleIncludeComponentTypeRequested,
  handleIncludeReferenceTypeRequested
);
